import { createContext, useContext, useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { authService, refreshAccessToken, tokenManager } from '../services/authApi'
import { extractUserFromJWT, isTokenExpired } from '../utils/jwtDecoder'

const AuthContext = createContext({})

// Sentry needs a stable id or it falls back to identifying people by IP address, which makes
// the users-affected count on an issue meaningless. extractUserFromJWT exposes the subject
// claim as `id` — there is no `userId` on what it returns. Every sign-in path goes through
// here so the shape cannot drift apart again.
const identifyToSentry = (userFromJWT) => {
  Sentry.setUser({
    id: userFromJWT.id,
    email: userFromJWT.email,
    username: userFromJWT.firstName,
  })
}

// Three separate paths read this cached list back. Only one of them used to guard the parse,
// so a value that would not parse reached checkAuthStatus's outer catch and signed the user
// out — a single bad write cost the session, and for an administrator nothing overwrites it
// first because /auth/me returns managedBuildings: null for that role. Anything unreadable is
// treated as "no cached list" and refilled from /auth/me or /Building. The Array.isArray check
// covers a cached "null", which parses cleanly and would otherwise put a null on the context
// where every consumer expects an array.
const readCachedManagedBuildings = () => {
  const raw = localStorage.getItem('managedBuildings')
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [userRole, setUserRole] = useState(null) // "Administrator" | "BuildingManager" | null
  const [managedBuildings, setManagedBuildings] = useState([])
  const [availableBuildings, setAvailableBuildings] = useState([])
  const [buildingsLoading, setBuildingsLoading] = useState(false)
  // Whether the last attempt to load the list failed. An empty availableBuildings on its own
  // cannot say why it is empty, and the switcher has to tell "there are none" apart from "we
  // could not find out" — the second is recoverable and the user deserves the retry. A flag
  // rather than the error: nothing user-facing should carry raw server text, and retrying is
  // the only offer worth making whichever way the request failed.
  const [buildingsError, setBuildingsError] = useState(false)
  const [selectedBuildingId, setSelectedBuildingIdState] = useState(
    () => localStorage.getItem('selectedBuildingId') || null
  )
  // The building the user actually chose, when the caller knew it. availableBuildings holds
  // only the first 200 buildings for an administrator, and the switcher's search deliberately
  // reaches past them, so a searched building can be selected without ever appearing there.
  const [selectedBuildingDetail, setSelectedBuildingDetail] = useState(null)

  useEffect(() => {
    checkAuthStatus()
  }, [])

  // Accepts either the whole building or a bare id — the four internal callers below only
  // ever have an id. An id on its own must drop the remembered building: reconcile can move
  // the selection, and a detail left behind would name the previous building against the new
  // id, which is worse than showing no name at all.
  const setSelectedBuilding = (building) => {
    const buildingId = typeof building === 'string' ? building : building?.buildingId || null
    setSelectedBuildingIdState(buildingId)
    setSelectedBuildingDetail(building && typeof building === 'object' ? building : null)
    if (buildingId) {
      localStorage.setItem('selectedBuildingId', buildingId)
    } else {
      localStorage.removeItem('selectedBuildingId')
    }
  }

  // The stored building id outlives the session that justified it, so it can point at a
  // building the user has since lost access to. api.js sends it as the CurrentBuildingId
  // header on every request and the server answers 403 for a manager who is no longer a
  // member, which breaks every building-scoped page until the user switches by hand.
  // Only call this with a list we actually received — passing an empty list that merely
  // means "the fetch failed" would clear a perfectly good selection.
  const reconcileSelectedBuilding = (buildings) => {
    const storedId = localStorage.getItem('selectedBuildingId')
    if (storedId && buildings.some(b => b.buildingId === storedId)) {
      return
    }
    setSelectedBuilding(buildings.length > 0 ? buildings[0].buildingId : null)
  }

  // An administrator's list is only the first 200 buildings, so absence from it does not mean
  // the building is gone — the switcher's search deliberately reaches past that page, and a
  // building picked there is expected to be missing. Ask the server about the id itself rather
  // than reading the reconcile above as authoritative. Returns the building when the server has
  // it, 'gone' when the server says it does not, and null when the answer settled nothing.
  const lookUpBuilding = async (apiModule, buildingId) => {
    try {
      const { data } = await apiModule.get(`/Building/${buildingId}`)
      return {
        buildingId: data.buildingId || data.id,
        name: data.name || data.userName,
        userName: data.userName,
        address: data.address
      }
    } catch (e) {
      return e.response?.status === 404 ? 'gone' : null
    }
  }

  // The administrator half of reconcileSelectedBuilding. Same intent — never leave a stale id
  // going out as the CurrentBuildingId header — but the membership check alone cannot decide
  // it here, so a missing id costs one confirming request before the selection is moved.
  const reconcileSelectedBuildingForAdmin = async (buildings, apiModule) => {
    const storedId = localStorage.getItem('selectedBuildingId')
    const selectFirst = () =>
      setSelectedBuilding(buildings.length > 0 ? buildings[0].buildingId : null)

    if (!storedId) {
      selectFirst()
      return
    }
    if (buildings.some(b => b.buildingId === storedId)) {
      return
    }

    const confirmed = await lookUpBuilding(apiModule, storedId)
    if (confirmed === 'gone') {
      selectFirst()
    } else if (confirmed) {
      // Re-selecting with the whole building, not the bare id, also restores the name: the
      // detail behind a searched choice is state, so it does not survive the reload that
      // brought us here, and the header has nothing to show without it.
      setSelectedBuilding(confirmed)
    }
    // confirmed === null — inconclusive. Discarding a working selection over a failed request
    // is the same mistake the catch below avoids; leave it exactly as it is.
  }

  const fetchAvailableBuildings = async (role, buildings) => {
    if (role === 'Administrator') {
      setBuildingsLoading(true)
      // Clear it on the way in, so a retry that works takes the error off the header.
      setBuildingsError(false)
      try {
        const { default: apiModule } = await import('../services/api')
        const response = await apiModule.get('/Building', { params: { pageSize: 200 } })
        const items = response.data.items || []
        const mapped = items.map(b => ({
          buildingId: b.id || b.buildingId,
          name: b.name || b.userName,
          userName: b.userName,
          address: b.address
        }))
        setAvailableBuildings(mapped)
        await reconcileSelectedBuildingForAdmin(mapped, apiModule)
      } catch (e) {
        console.error('Failed to fetch buildings for admin:', e)
        // The list stays empty either way; this is what stops that emptiness being read as
        // "this administrator manages nothing" and silently removing the switcher.
        setBuildingsError(true)
      } finally {
        setBuildingsLoading(false)
      }
    } else if (buildings?.length > 0) {
      setAvailableBuildings(buildings)
      reconcileSelectedBuilding(buildings)
    }
  }

  const refreshBuildings = () => {
    const currentRole = localStorage.getItem('userRole')
    return fetchAvailableBuildings(currentRole, readCachedManagedBuildings())
  }

  const checkAuthStatus = async () => {
    try {
      const token = tokenManager.getToken()

      if (token) {
        // Check if token is expired
        if (isTokenExpired(token)) {
          console.log('Token expired, attempting refresh...')
          const refreshSuccess = await refreshToken()
          if (!refreshSuccess) {
            logout()
            return
          }
          // Get the new token after refresh
          const newToken = tokenManager.getToken()
          if (newToken) {
            const userFromJWT = extractUserFromJWT(newToken)
            if (userFromJWT) {
              setUser(userFromJWT)
              setIsAuthenticated(true)
              // Update stored profile
              tokenManager.setUserProfile(userFromJWT)
              // Set Sentry user context
              identifyToSentry(userFromJWT)
            }
          }
        } else {
          // Token is valid, extract user data from JWT
          const userFromJWT = extractUserFromJWT(token)
          if (userFromJWT) {
            setUser(userFromJWT)
            setIsAuthenticated(true)
            // Update stored profile
            tokenManager.setUserProfile(userFromJWT)
            // Set Sentry user context
            identifyToSentry(userFromJWT)
          } else {
            // Invalid token structure
            logout()
          }
        }

        // Restore role and buildings from localStorage
        const storedRole = localStorage.getItem('userRole')
        const storedBuildings = readCachedManagedBuildings()
        if (storedRole) {
          setUserRole(storedRole)
        }
        setManagedBuildings(storedBuildings)
        // Auto-select first building if none selected
        if (!localStorage.getItem('selectedBuildingId') && storedBuildings.length > 0) {
          setSelectedBuilding(storedBuildings[0].buildingId)
        }

        // Fetch fresh data from /me endpoint
        try {
          const { default: api } = await import('../services/api')
          const meResponse = await api.get('/auth/me')
          const meData = meResponse.data
          if (meData.role) {
            setUserRole(meData.role)
            localStorage.setItem('userRole', meData.role)
          }
          if (meData.managedBuildings) {
            setManagedBuildings(meData.managedBuildings)
            localStorage.setItem('managedBuildings', JSON.stringify(meData.managedBuildings))
            // Authoritative for a manager, and the only place that sees an empty managed
            // list — /me returns null here for administrators, who reconcile against the
            // full /Building list instead.
            reconcileSelectedBuilding(meData.managedBuildings)
          }
        } catch (e) {
          // /me endpoint may fail if token was just refreshed, use cached data
          console.log('Could not fetch /me endpoint, using cached role data')
        }

        // Fetch available buildings for the global building selector. Re-read rather than
        // reuse storedBuildings — /auth/me may have just written a fresher list.
        const effectiveRole = localStorage.getItem('userRole')
        await fetchAvailableBuildings(effectiveRole, readCachedManagedBuildings())
      }
    } catch (error) {
      console.error('Auth check failed:', error)
      logout()
    } finally {
      setLoading(false)
    }
  }

  const processLoginResponse = async (responseData) => {
    const { token, refreshToken, access_token, refresh_token, role, managedBuildings: buildings } = responseData

    // Handle different possible token field names
    const accessToken = token || access_token
    const refToken = refreshToken || refresh_token

    if (!accessToken) {
      throw new Error('No token received from server')
    }

    tokenManager.setTokens(accessToken, refToken)

    const userFromJWT = extractUserFromJWT(accessToken)
    if (!userFromJWT) {
      throw new Error('Could not extract user data from token')
    }

    tokenManager.setUserProfile(userFromJWT)
    setUser(userFromJWT)
    setIsAuthenticated(true)

    // Set Sentry user context
    identifyToSentry(userFromJWT)

    // Store role and managed buildings
    if (role) {
      setUserRole(role)
      localStorage.setItem('userRole', role)
    }
    if (buildings) {
      setManagedBuildings(buildings)
      localStorage.setItem('managedBuildings', JSON.stringify(buildings))
      if (buildings.length > 0) {
        setSelectedBuilding(buildings[0].buildingId)
      }
    }

    // Fetch available buildings for the global building selector
    await fetchAvailableBuildings(role, buildings || [])

    return { success: true, data: responseData, user: userFromJWT }
  }

  const extractErrorMessage = (error, fallback) => {
    const data = error.response?.data
    const msg = typeof data === 'string' ? data
      : data?.message || data?.detail || data?.title
      || error.message
    return msg || fallback
  }

  const login = async (credentials) => {
    try {
      const response = await authService.login(credentials)
      return await processLoginResponse(response.data)
    } catch (error) {
      console.error('Login failed:', error)
      return { success: false, error: extractErrorMessage(error, 'Login failed') }
    }
  }

  const loginWithGoogle = async (credential) => {
    try {
      const response = await authService.externalLogin({ provider: 'Google', idToken: credential })
      return await processLoginResponse(response.data)
    } catch (error) {
      console.error('Google login failed:', error)
      return { success: false, error: extractErrorMessage(error, 'Google login failed') }
    }
  }

  const logout = () => {
    tokenManager.clearTokens()
    localStorage.removeItem('userRole')
    localStorage.removeItem('managedBuildings')
    localStorage.removeItem('selectedBuildingId')
    Sentry.setUser(null)
    setUser(null)
    setIsAuthenticated(false)
    setUserRole(null)
    setManagedBuildings([])
    setAvailableBuildings([])
    setBuildingsError(false)
    setSelectedBuildingIdState(null)
    setSelectedBuildingDetail(null)
  }

  const refreshToken = async () => {
    try {
      // Shares the single in-flight refresh with the API interceptor. Both paths can fire
      // at once — checkAuthStatus renews an expired token on page load while that same
      // page's first requests are already coming back 401 — and the server honours a
      // given refresh token exactly once.
      const newToken = await refreshAccessToken()

      if (!newToken) {
        logout()
        return false
      }

      return true
    } catch (error) {
      console.error('Token refresh failed:', error)
      logout()
      return false
    }
  }

  const restoreDevSession = (sessionData) => {
    const { token, refreshToken: refToken, role, managedBuildings: buildings } = sessionData
    if (!token) return false

    tokenManager.setTokens(token, refToken)
    const userFromJWT = extractUserFromJWT(token)
    if (!userFromJWT) return false

    tokenManager.setUserProfile(userFromJWT)
    setUser(userFromJWT)
    setIsAuthenticated(true)
    // Restoring a dev session normally follows a logout, which resets the Sentry scope
    identifyToSentry(userFromJWT)
    if (role) {
      setUserRole(role)
      localStorage.setItem('userRole', role)
    }
    if (buildings) {
      setManagedBuildings(buildings)
      localStorage.setItem('managedBuildings', JSON.stringify(buildings))
      if (buildings.length > 0 && !localStorage.getItem('selectedBuildingId')) {
        setSelectedBuilding(buildings[0].buildingId)
      }
    }
    // Fetch available buildings (fire-and-forget, UI updates when ready)
    fetchAvailableBuildings(role, buildings || [])
    return true
  }

  const isAdmin = userRole === 'Administrator'
  const isBuildingManager = userRole === 'BuildingManager'
  // The loaded list wins where it has the building — it is refreshed from the server — and the
  // remembered choice covers everything beyond it. The id check is what keeps a remembered
  // building from naming a selection it is not.
  const selectedBuilding =
    availableBuildings.find(b => b.buildingId === selectedBuildingId)
    || (selectedBuildingDetail?.buildingId === selectedBuildingId ? selectedBuildingDetail : null)
  const selectedBuildingName = selectedBuilding?.name || ''

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    loginWithGoogle,
    logout,
    refreshToken,
    checkAuthStatus,
    restoreDevSession,
    userRole,
    isAdmin,
    isBuildingManager,
    managedBuildings,
    availableBuildings,
    buildingsLoading,
    buildingsError,
    selectedBuildingId,
    selectedBuilding,
    selectedBuildingName,
    setSelectedBuilding,
    refreshBuildings
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
